<template>
    <div class="sip-drawer-host" :hidden="!open">
        <div
            v-if="open"
            class="sip-drawer-overlay"
            :class="overlayClasses"
            :style="overlayStyle"
            @click.self="onBackdrop"
            role="presentation"
        >
            <aside
                class="sip-drawer"
                :class="drawerClasses"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="titleId"
                @click.stop
            >
                <header class="sip-drawer-header" v-if="showHeader">
                    <h5 class="sip-drawer-title" :id="titleId">
                        <slot name="title">
                            <em v-if="icon" :class="['icon', 'ni', icon, 'me-2']" aria-hidden="true"></em>
                            {{ title }}
                        </slot>
                    </h5>
                    <div class="sip-drawer-header-actions" v-if="$slots.actions">
                        <slot name="actions"></slot>
                    </div>
                    <button
                        type="button"
                        class="sip-drawer-close"
                        @click="close"
                        :aria-label="closeLabel"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                        </svg>
                    </button>
                </header>
                <div class="sip-drawer-body" ref="body">
                    <slot></slot>
                </div>
                <footer class="sip-drawer-footer" v-if="$slots.footer">
                    <slot name="footer"></slot>
                </footer>
            </aside>
        </div>
    </div>
</template>

<script>
    const DROPDOWN_Z = '2100';
    const DROPDOWN_SEL = [
        '.multiselect--active .multiselect__content-wrapper',
        '.vdp-datepicker__calendar'
    ].join(',');

    let lockCount = 0;
    let previousOverflow = '';
    let openDrawerCount = 0;

    function lockBodyScroll() {
        if (lockCount === 0) {
            previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        lockCount += 1;
    }

    function unlockBodyScroll() {
        lockCount = Math.max(0, lockCount - 1);
        if (lockCount === 0) {
            document.body.style.overflow = previousOverflow || '';
        }
    }

    export default {
        name: 'side-drawer',
        props: {
            open: {
                type: Boolean,
                default: false
            },
            title: {
                type: String,
                default: ''
            },
            icon: {
                type: String,
                default: ''
            },
            size: {
                type: String,
                default: 'md',
                validator(value) {
                    return ['sm', 'md', 'lg', 'xl', 'full'].indexOf(value) !== -1;
                }
            },
            persistent: {
                type: Boolean,
                default: false
            },
            showHeader: {
                type: Boolean,
                default: true
            },
            overlayClass: {
                type: String,
                default: ''
            },
            drawerClass: {
                type: String,
                default: ''
            },
            closeLabel: {
                type: String,
                default: 'Close'
            },
            appendToBody: {
                type: Boolean,
                default: true
            },
            level: {
                type: Number,
                default: null
            }
        },
        data() {
            return {
                stackLevel: 1,
                wasOpen: false
            };
        },
        mounted() {
            this.mountToBody();
        },
        computed: {
            titleId() {
                return 'sip-drawer-title-' + this._uid;
            },
            sizeClass() {
                return 'sip-drawer--' + (this.size || 'md');
            },
            resolvedLevel() {
                return this.level != null ? this.level : this.stackLevel;
            },
            isNested() {
                return this.resolvedLevel > 1;
            },
            overlayClasses() {
                const classes = [];
                if (this.overlayClass) {
                    classes.push(this.overlayClass);
                }
                if (this.isNested) {
                    classes.push('sip-drawer-overlay--nested');
                    classes.push('sip-drawer-overlay--level-' + this.resolvedLevel);
                }
                return classes;
            },
            drawerClasses() {
                const classes = [this.sizeClass];
                if (this.drawerClass) {
                    classes.push(this.drawerClass);
                }
                if (this.isNested) {
                    classes.push('sip-drawer--nested');
                    classes.push('sip-drawer--level-' + this.resolvedLevel);
                }
                return classes;
            },
            overlayStyle() {
                // Theme CSS: z-index: calc(<base> + var(--sip-drawer-z-offset)).
                // Offset alone keeps mobile (base 1140) and desktop (base 1070) stacking correct.
                const offset = Math.max(0, this.resolvedLevel - 1) * 20;
                return {
                    '--sip-drawer-z-offset': String(offset)
                };
            }
        },
        watch: {
            open: {
                immediate: true,
                handler(isOpen) {
                    if (isOpen) {
                        if (this.level == null) {
                            openDrawerCount += 1;
                            this.stackLevel = openDrawerCount;
                        } else {
                            this.stackLevel = this.level;
                        }
                        this.wasOpen = true;
                        document.addEventListener('keydown', this.onKeydown);
                        lockBodyScroll();
                        document.documentElement.classList.add('sip-drawer-open');
                        if (this.isNested) {
                            document.documentElement.classList.add('sip-drawer-nested-open');
                        }
                        this.$nextTick(() => {
                            this.mountToBody();
                            this.bindDropdownBoundary();
                        });
                    } else {
                        // Only tear down if this instance was previously open.
                        // Nested drawers mount closed inside a parent; running cleanup
                        // on first render would unlock scroll and drop openDrawerCount.
                        if (!this.wasOpen) {
                            return;
                        }
                        this.wasOpen = false;
                        document.removeEventListener('keydown', this.onKeydown);
                        unlockBodyScroll();
                        this.unbindDropdownBoundary();
                        if (this.level == null && this.stackLevel > 0) {
                            openDrawerCount = Math.max(0, openDrawerCount - 1);
                        }
                        if (openDrawerCount <= 0) {
                            document.documentElement.classList.remove('sip-drawer-open');
                            document.documentElement.classList.remove('sip-drawer-nested-open');
                        } else if (openDrawerCount < 2) {
                            document.documentElement.classList.remove('sip-drawer-nested-open');
                        }
                    }
                }
            }
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.onKeydown);
            if (this.open || this.wasOpen) {
                unlockBodyScroll();
                if (this.level == null && this.stackLevel > 0) {
                    openDrawerCount = Math.max(0, openDrawerCount - 1);
                }
                if (openDrawerCount <= 0) {
                    document.documentElement.classList.remove('sip-drawer-open');
                    document.documentElement.classList.remove('sip-drawer-nested-open');
                } else if (openDrawerCount < 2) {
                    document.documentElement.classList.remove('sip-drawer-nested-open');
                }
            }
            this.unbindDropdownBoundary();
            // Remove body-ported host so closed nested hosts don't linger
            if (this.appendToBody && this.$el && this.$el.parentNode === document.body) {
                document.body.removeChild(this.$el);
            }
        },
        methods: {
            mountToBody() {
                if (!this.appendToBody || typeof document === 'undefined') {
                    return;
                }
                const el = this.$el;
                if (!el || el.nodeType !== 1) {
                    return;
                }
                if (el.parentNode !== document.body) {
                    document.body.appendChild(el);
                }
            },
            close() {
                this.$emit('close');
            },
            onBackdrop() {
                if (this.persistent) {
                    return;
                }
                // Ignore backdrop while a BV modal (nested "Add New") is open,
                // or while a higher stacked Vue drawer is above this one.
                if (document.body.classList.contains('modal-open') ||
                    document.querySelector('.modal.show, .modal.d-block')) {
                    return;
                }
                if (this.level == null && this.stackLevel !== openDrawerCount) {
                    return;
                }
                this.close();
            },
            onKeydown(event) {
                if (event.key === 'Escape' || event.keyCode === 27) {
                    if (this.persistent) {
                        return;
                    }
                    // Let Bootstrap Vue / stacked modals own Escape first
                    if (document.body.classList.contains('modal-open') ||
                        document.querySelector('.modal.show, .modal.d-block')) {
                        return;
                    }
                    // Only the topmost drawer handles Escape
                    if (this.level == null && this.stackLevel !== openDrawerCount) {
                        return;
                    }
                    event.preventDefault();
                    this.close();
                }
            },
            bindDropdownBoundary() {
                this.unbindDropdownBoundary();
                if (!this.$el || this.$el.nodeType !== 1) {
                    return;
                }
                this._onReposition = () => {
                    if (this._dropdownRaf) {
                        return;
                    }
                    this._dropdownRaf = requestAnimationFrame(() => {
                        this._dropdownRaf = null;
                        this.positionDropdowns();
                    });
                };
                this._dropdownObserver = new MutationObserver(this._onReposition);
                this._dropdownObserver.observe(this.$el, {
                    subtree: true,
                    childList: true,
                    attributes: true,
                    attributeFilter: ['class', 'style']
                });
                const body = this.$refs.body;
                if (body) {
                    body.addEventListener('scroll', this._onReposition, { passive: true });
                }
                window.addEventListener('resize', this._onReposition);
                this.positionDropdowns();
            },
            unbindDropdownBoundary() {
                if (this._dropdownObserver) {
                    this._dropdownObserver.disconnect();
                    this._dropdownObserver = null;
                }
                if (this._dropdownRaf) {
                    cancelAnimationFrame(this._dropdownRaf);
                    this._dropdownRaf = null;
                }
                if (this._onReposition) {
                    const body = this.$refs.body;
                    if (body) {
                        body.removeEventListener('scroll', this._onReposition);
                    }
                    window.removeEventListener('resize', this._onReposition);
                    this._onReposition = null;
                }
            },
            positionDropdowns() {
                if (this._positioning || !this.$el || this.$el.nodeType !== 1) {
                    return;
                }
                this._positioning = true;
                const nodes = this.$el.querySelectorAll(DROPDOWN_SEL);
                for (let i = 0; i < nodes.length; i++) {
                    const el = nodes[i];
                    const hidden = el.style && el.style.display === 'none';
                    if (hidden) {
                        continue;
                    }
                    const anchor = el.classList.contains('vdp-datepicker__calendar')
                        ? (el.closest('.vdp-datepicker') || el.parentElement)
                        : (el.closest('.multiselect') || el.parentElement);
                    if (!anchor) {
                        continue;
                    }
                    const rect = anchor.getBoundingClientRect();
                    const maxH = Math.min(240, Math.max(120, window.innerHeight - 24));
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const openUp = spaceBelow < Math.min(maxH, 180) && rect.top > spaceBelow;
                    // Use !important so theme %-based top/bottom cannot shove fixed menus off-screen.
                    el.style.setProperty('position', 'fixed', 'important');
                    el.style.setProperty('z-index', DROPDOWN_Z, 'important');
                    el.style.setProperty('left', Math.max(8, rect.left) + 'px', 'important');
                    el.style.setProperty('width', Math.max(rect.width, 160) + 'px', 'important');
                    el.style.setProperty('right', 'auto', 'important');
                    el.style.setProperty('max-height', maxH + 'px', 'important');
                    el.style.setProperty('overflow-y', 'auto', 'important');
                    if (openUp) {
                        el.style.setProperty('top', 'auto', 'important');
                        el.style.setProperty('bottom', (window.innerHeight - rect.top + 4) + 'px', 'important');
                    } else {
                        el.style.setProperty('bottom', 'auto', 'important');
                        el.style.setProperty('top', (rect.bottom + 4) + 'px', 'important');
                    }
                }
                this._positioning = false;
            }
        }
    }
</script>
